import {
  CustomLayoutDrawerContent,
  DrawerBreadCrumbs,
} from 'module/ui/components/atoms/drawer';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import {
  CustomSectionContentState,
  CustomSectionType,
  PageTemplateSectionSource,
} from 'api/custom-section/types';
import { PageTemplateLite } from 'api/page-template/types';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { CompanyPageDerivedSectionSourcesUpdaterProps } from 'components/organisms/document-editor/extensions/custom-section/types';
import { SectionSourcRenderer } from './source-render';
import { DerivedSectionSourcesSelector } from './source-selector';
import { defaultConfiguration } from '../../types';

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
`;

interface Props extends CompanyPageDerivedSectionSourcesUpdaterProps {
  pageTemplateSources: PageTemplateLite[];
}

export const CompanyPageSectionSourcesUpdater: FC<Props> = ({
  onCancel,
  node,
  editor,
  nodePosition,
  onUpdate,
  pageTemplateSources,
}) => {
  const breadcrumbs = useMemo(() => {
    return [
      {
        title: node.attrs.title,
      },
    ];
  }, [node.attrs.title]);
  const [sources, setSources] = useState<PageTemplateSectionSource[]>([]);

  useEffect(() => {
    setSources(node.attrs.config?.sourcePageTemplateSections ?? []);
  }, [node.attrs.config?.sourcePageTemplateSections]);

  const onDone = useCallback(() => {
    onUpdate(sources);
  }, [onUpdate, sources]);
  const onInsertAsNewSection = useCallback(
    (source: PageTemplateSectionSource) => {
      editor
        .chain()
        .addCustomSection({
          attributes: {
            title: source.pageTemplateSectionTitle,
            contentState: CustomSectionContentState.Prompt,
            sectionType: CustomSectionType.CompanyPageSourceDerivedSection,
            config: {
              ...defaultConfiguration,
              ...node.attrs.config,
              sourcePageTemplateSections: [source],
            },
          },
          insertAt: nodePosition + 1,
        })
        .run();
    },
    [editor, node.attrs.config, nodePosition],
  );
  const onRemove = useCallback(
    (source: PageTemplateSectionSource) => {
      setSources(
        sources.filter(
          (s) => s.pageTemplateSectionId !== source.pageTemplateSectionId,
        ),
      );
    },
    [sources],
  );

  return (
    <CustomLayoutDrawerContent>
      <CustomLayoutDrawerContent.Title
        breadcrumbs={<DrawerBreadCrumbs breadcrumbs={breadcrumbs} />}
      >
        <FormattedMessage id="sources" />
      </CustomLayoutDrawerContent.Title>
      <CustomLayoutDrawerContent.Content>
        <SectionWrapper>
          <DerivedSectionSourcesSelector
            node={node}
            onChange={setSources}
            pageTemplateSources={pageTemplateSources}
            value={sources}
          />
          {sources.map((source) => (
            <SectionSourcRenderer
              key={source.pageTemplateSectionId}
              source={source}
              onRemove={() => onRemove(source)}
              onInsertAsNewSection={() => onInsertAsNewSection(source)}
            />
          ))}
        </SectionWrapper>
      </CustomLayoutDrawerContent.Content>
      <CustomLayoutDrawerContent.Footer $rightAlignedAction>
        <Button onClick={onCancel}>
          <FormattedMessage id="back" />
        </Button>
        <Button
          variant="primary"
          leftIcon={<FontAwesomeIcon icon={faCheck} size={12} />}
          onClick={onDone}
        >
          <FormattedMessage id="done" />
        </Button>
      </CustomLayoutDrawerContent.Footer>
    </CustomLayoutDrawerContent>
  );
};
